import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import preloadingService from '../services/PreloadingService';
import Logger from '../utils/intelligentLogger';

/**
 * Hook for managing page preloading
 */
export const usePreloading = () => {
  const location = useLocation();
  const hasStartedPreloading = useRef(false);
  const navigationStartTime = useRef<number>(0);

  /**
   * Start background preloading after current page loads
   */
  const startBackgroundPreloading = useCallback((currentPath?: string) => {
    if (hasStartedPreloading.current) {
      return;
    }

    hasStartedPreloading.current = true;
    preloadingService.startBackgroundPreloading(currentPath);
  }, []);

  /**
   * Navigate to page with cache-first strategy
   */
  const navigateTo = useCallback((path: string) => {
    const startTime = Date.now();
    navigationStartTime.current = startTime;

    // Check if page is preloaded
    const preloadedPage = preloadingService.getPreloadedPage(path);
    
    if (preloadedPage) {
      // Instant navigation from cache
      Logger.throttle(`nav-${path}`, `Instant navigation to ${path} (from cache)`);
      preloadingService.trackNavigation(path, startTime);
      return { component: preloadedPage, fromCache: true };
    } else {
      // Load page normally (fallback) - only log in debug mode
      Logger.debug(`Loading ${path} normally (not cached)`);
      return { component: null, fromCache: false };
    }
  }, []);

  /**
   * Get preloaded page component
   */
  const getPreloadedPage = useCallback((path: string) => {
    return preloadingService.getPreloadedPage(path);
  }, []);

  /**
   * Invalidate cache for specific page or all pages
   */
  const invalidateCache = useCallback((path?: string) => {
    preloadingService.invalidate(path);
  }, []);

  /**
   * Get cache statistics
   */
  const getCacheStats = useCallback(() => {
    return preloadingService.getCacheStats();
  }, []);

  /**
   * Stop preloading
   */
  const stopPreloading = useCallback(() => {
    preloadingService.stopPreloading();
  }, []);

  // Start preloading when location changes
  useEffect(() => {
    const currentPath = location.pathname;
    
    // Start background preloading after a short delay
    const timer = setTimeout(() => {
      startBackgroundPreloading(currentPath);
    }, 500); // Reduced delay for faster preloading

    return () => {
      clearTimeout(timer);
    };
  }, [location.pathname, startBackgroundPreloading]);

  // Reset preloading flag when location changes significantly
  useEffect(() => {
    hasStartedPreloading.current = false;
  }, [location.pathname]);

  return {
    navigateTo,
    getPreloadedPage,
    invalidateCache,
    getCacheStats,
    stopPreloading,
    startBackgroundPreloading,
    isPreloading: preloadingService.getCacheStats().isPreloading
  };
};

/**
 * Hook for preloading-aware navigation
 */
export const usePreloadingNavigation = () => {
  const { navigateTo, getPreloadedPage } = usePreloading();

  const navigateWithPreloading = useCallback((path: string, navigate: (path: string) => void) => {
    const result = navigateTo(path);
    
    if (result.fromCache) {
      // Page is cached, navigate instantly
      navigate(path);
      return { instant: true };
    } else {
      // Page not cached, navigate normally
      navigate(path);
      return { instant: false };
    }
  }, [navigateTo]);

  return {
    navigateWithPreloading,
    getPreloadedPage
  };
}; 